import { useCallback, useEffect, useMemo } from 'react'
import { create } from 'zustand'
import { useQuery } from '@tanstack/react-query'
import { useAppStore } from '@/store/app'
import { useRole } from '@/hooks/useRole'
import { billingApi } from '@/api/billing'
import { rolesAllow } from '@/utils/permissions'
import { RELEASES, type ChangeEntry, type Release } from '@/changelog'

const PLATFORM_ORG_ID = 'multiaccess'

// Seen entry ids, per user, in browser storage. Losing it (private window,
// cleared storage) only means old entries show as new again — never breaks
// the page. Moves server-side once read tracking needs to follow users.
const storageKey = (email: string) => `multiaccess.whatsNew.seen.${email}`

function readSeen(email: string): Set<string> {
  try {
    const raw = localStorage.getItem(storageKey(email))
    return new Set(raw ? (JSON.parse(raw) as string[]) : [])
  } catch {
    return new Set()
  }
}

function writeSeen(email: string, ids: Set<string>) {
  try {
    localStorage.setItem(storageKey(email), JSON.stringify([...ids]))
  } catch {
    // storage unavailable — keep the in-memory state only
  }
}

interface SeenState {
  email: string | null
  seen: Set<string>
  load: (email: string) => void
  markSeen: (ids: string[]) => void
}

const useSeenStore = create<SeenState>((set, get) => ({
  email: null,
  seen: new Set(),
  load: email => {
    if (get().email !== email) set({ email, seen: readSeen(email) })
  },
  markSeen: ids => {
    const { email, seen } = get()
    if (!email || ids.every(id => seen.has(id))) return
    const next = new Set(seen)
    ids.forEach(id => next.add(id))
    writeSeen(email, next)
    set({ seen: next })
  },
}))

/**
 * Releases filtered to what this user should see (roles, platform org, trial
 * status, subscribed services), plus which entries they haven't seen yet.
 */
export function useWhatsNew() {
  const { user } = useAppStore()
  const { isOrgAdmin } = useRole()
  const { seen, load, markSeen } = useSeenStore()

  useEffect(() => { if (user?.email) load(user.email) }, [user?.email, load])

  const needsTrialStatus = RELEASES.some(r => r.entries.some(e => e.trialOnly))
  // Billing is admin-only on the backend; for anyone else, trial-only entries
  // simply stay hidden rather than probing an endpoint that would 403.
  const { data: subscription } = useQuery({
    queryKey: ['whats-new-subscription', user?.orgId],
    queryFn: () => billingApi.subscription(String(user!.orgId)),
    enabled: needsTrialStatus && isOrgAdmin && !!user?.orgId,
    staleTime: 10 * 60 * 1000,
    retry: false,
  })
  const isTrial = subscription?.status === 'trial'
  const isPlatformOrg = user?.orgId === PLATFORM_ORG_ID
  const services = user?.subscribedServices
  const roles = user?.roleName
  const legacyRole = user?.role

  const releases = useMemo<Release[]>(() => {
    const visible = (e: ChangeEntry) =>
      (!e.roles || rolesAllow(roles, e.roles, legacyRole)) &&
      (!e.platformOnly || isPlatformOrg) &&
      (!e.trialOnly || isTrial) &&
      // Services load asynchronously — don't hide entries before they arrive.
      (!e.service || !services || services.includes(e.service))
    return RELEASES
      .map(r => ({ ...r, entries: r.entries.filter(visible) }))
      .filter(r => r.entries.length > 0)
  }, [roles, legacyRole, isPlatformOrg, isTrial, services])

  const unseenIds = useMemo(
    () => new Set(releases.flatMap(r => r.entries.map(e => e.id)).filter(id => !seen.has(id))),
    [releases, seen],
  )

  const markAllSeen = useCallback(() => markSeen(releases.flatMap(r => r.entries.map(e => e.id))), [releases, markSeen])

  return { releases, unseenIds, unseenCount: unseenIds.size, markAllSeen }
}
